import {
  VIVA_SIGNATURE_DEFAULTS,
  VIVA_SIGNATURE_ICON_BASE,
  VIVA_SIGNATURE_LOGO_URL,
  VIVA_SIGNATURE_LOGO_WIDTH_PX,
  resolveSignatureFontStack,
} from './constants.js'

const NAME_COLOR = '#111111'
const BODY_COLOR = '#111111'
const DIVIDER_COLOR = '#333333'
const LINK_COLOR = '#111111'

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function normalizeWebsiteUrl(url) {
  const trimmed = String(url || '').trim()
  if (!trimmed) return VIVA_SIGNATURE_DEFAULTS.websiteUrl
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed.replace(/^\/+/, '')}`
}

function websiteLabel(url) {
  const normalized = normalizeWebsiteUrl(url)
  return normalized.replace(/^https?:\/\//i, '').replace(/\/$/, '') || 'www.vivapsychotherapy.com'
}

function phoneHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 10) return `tel:+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`
  return `tel:+${digits.replace(/^\+/, '')}`
}

function mapsHref(address) {
  const query = String(address || '').trim()
  if (!query) return ''
  return `https://maps.google.com/?q=${encodeURIComponent(query)}`
}

function iconSrc(base, name) {
  return `${base}/${name}-pos.svg`
}

function contactIconHtml(kind, assetBase) {
  const iconName =
    kind === 'phone' ? 'phone' : kind === 'email' ? 'mail' : kind === 'website' ? 'web' : 'pin'
  const alt =
    kind === 'phone' ? 'Phone' : kind === 'email' ? 'Email' : kind === 'website' ? 'Website' : 'Address'
  const src = iconSrc(assetBase, iconName)
  return `<img src="${src}" alt="${alt}" width="14" height="14" style="display: block; border: 0; width: 14px; height: 14px;" />`
}

function contactRow(kind, href, label, fontStack, assetBase, { linked = true } = {}) {
  if (!String(label || '').trim()) return ''

  const safeLabel = escapeHtml(String(label).trim())
  const content =
    linked && href
      ? `<a href="${escapeHtml(href)}"${kind === 'website' || kind === 'address' ? ' target="_blank"' : ''} style="color: ${LINK_COLOR}; text-decoration: underline;">${safeLabel}</a>`
      : `<span style="color: ${BODY_COLOR};">${safeLabel}</span>`

  return `<tr>
    <td style="padding-bottom: 3px;">
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; text-align: left;">
        <tr>
          <td valign="middle" style="width: 22px; text-align: left; padding-right: 8px;">
            ${contactIconHtml(kind, assetBase)}
          </td>
          <td valign="middle" style="font-family: ${fontStack}; font-size: 12px; color: ${BODY_COLOR}; line-height: 1.35;">
            ${content}
          </td>
        </tr>
      </table>
    </td>
  </tr>`
}

function roleRows(role, fontStack) {
  const lines = String(role || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  return lines
    .map(
      (line, index) =>
        `<tr><td style="padding-bottom: ${index === lines.length - 1 ? '10px' : '1px'};"><span style="font-family: ${fontStack}; font-size: 13px; color: ${BODY_COLOR}; font-weight: normal;">${escapeHtml(line)}</span></td></tr>`
    )
    .join('')
}

export function emptySignatureFields() {
  return { ...VIVA_SIGNATURE_DEFAULTS }
}

export function buildSignatureHtml(options) {
  const {
    fields,
    assetBase = VIVA_SIGNATURE_ICON_BASE,
    logoUrl = VIVA_SIGNATURE_LOGO_URL,
  } = options

  const fontStack = resolveSignatureFontStack(fields.fontKey)
  const name = String(fields.name || '').trim()
  const phone = String(fields.phone || '').trim()
  const email = String(fields.email || '').trim()
  const address = String(fields.address || '').trim()
  const websiteUrl = normalizeWebsiteUrl(fields.websiteUrl)
  const websiteText = websiteLabel(fields.websiteUrl)

  const phoneRow = contactRow('phone', phoneHref(phone), phone, fontStack, assetBase)
  const emailRow = contactRow('email', email ? `mailto:${email}` : '', email, fontStack, assetBase)
  const webRow = contactRow('website', websiteUrl, websiteText, fontStack, assetBase)
  const addressRow = contactRow('address', mapsHref(address), address, fontStack, assetBase)

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family: ${fontStack}; border-collapse: collapse; line-height: 1.35; text-align: left; background-color: #ffffff;">
  <tr>
    <td valign="top" style="padding-right: 20px; text-align: center; width: ${VIVA_SIGNATURE_LOGO_WIDTH_PX}px;">
      <a href="${escapeHtml(websiteUrl)}" target="_blank" style="text-decoration: none; display: block;">
        <img src="${escapeHtml(logoUrl)}" alt="VIVA Psychotherapy" width="${VIVA_SIGNATURE_LOGO_WIDTH_PX}" style="display: block; border: 0; width: ${VIVA_SIGNATURE_LOGO_WIDTH_PX}px; height: auto;" />
      </a>
    </td>
    <td valign="top">
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; text-align: left;">
        ${
          name
            ? `<tr><td style="padding-bottom: 4px;"><span style="font-family: ${fontStack}; font-size: 18px; font-weight: bold; color: ${NAME_COLOR};">${escapeHtml(name)}</span></td></tr>`
            : ''
        }
        ${roleRows(fields.role, fontStack)}
        <tr>
          <td style="padding-bottom: 10px; border-bottom: 1px solid ${DIVIDER_COLOR}; font-size: 1px; line-height: 1px;">&nbsp;</td>
        </tr>
        <tr>
          <td style="padding-top: 10px;">
            <table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse; text-align: left;">
              ${phoneRow}
              ${emailRow}
              ${webRow}
              ${addressRow}
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`
}
